<script>
    import get from 'lodash/get';
    import { minimumShortTermDate, calculateDateDefaults, defaultShortTerm, defaultShortTermEnd } from '../../modules/dates';
    import moment from '../../modules/moment';
    import { getViewData } from '../../modules/view-data';

    export default {
        computed: {
            /**
             * Gets space availability either from space object (Space modal)
             * or from window.YPS object (Space Advert, Booking form)
             */
            availability() {
                return get(this, 'space.availability', getViewData('availability'));
            },

            startDateConfig() {
                return this.filterConfig({
                    minDate: this.minStartDate(),
                    maxDate: this.maxStartDate(),
                });
            },

            startTimeConfig() {
                return this.filterConfig({
                    interval: 15,
                    min: this.minStartTime(this.start_date),
                    max: this.maxStartTime(this.start_date),
                });
            },

            endDateConfig() {
                const $vm = this;
                const startDateMoment = moment(this.start_date, 'DD-MM-YYYY');
                const endDateMoment = moment(this.end_date, 'DD-MM-YYYY');
                const minEndDate = this.minEndDate(this.start_date);

                return this.filterConfig({
                    minDate: minEndDate,
                    maxDate: this.maxEndDate(this.start_date),
                    onDayCreate: (dObj, dStr, fp, dayElem) => {
                        const dayMoment = moment(dayElem.dateObj);
                        const startDate = $vm.start_date;
                        const endDate = $vm.end_date;

                        if (dayMoment.isAfter(startDateMoment) && dayMoment.isBefore(endDateMoment)) {
                            dayElem.classList.add('selected-range');
                        }

                        const dmy = dayMoment.format('DD-MM-YYYY');
                        if (dmy === startDate) {
                            dayElem.classList.add('start-date');
                        }
                        if (dmy === endDate) {
                            dayElem.classList.add('end-date');
                        }
                    },
                });
            },

            endTimeConfig() {
                return this.filterConfig({
                    interval: 15,
                    min: this.minEndTime(this.start_date, this.start_time, this.end_date),
                    max: this.maxEndTime(this.start_date, this.start_time, this.end_date),
                });
            },

            allowsOvernight() {
                return this.space && this.space.overnight;
            },
        },

        methods: {
            /**
             * Reset dates to the default dates.
             */
            resetDates() {
                const defaultShortTermDate = defaultShortTerm().format('DD-MM-YYYY');
                this.start_date = defaultShortTermDate;
                this.end_date = defaultShortTermDate;

                calculateDateDefaults(this);
            },

            /**
             * Reset times to defaults
             */
            resetTimes() {
                this.start_time = defaultShortTerm().format('HH:mm');
                this.end_time = defaultShortTermEnd().format('HH:mm');
                calculateDateDefaults(this);
                this.constrainEndDateTime();
            },

            /**
             * Filter out undefined config items
             */
            filterConfig(config) {
                return Object.keys(config)
                    .filter((key) => config[key])
                    .reduce((object, key) => {
                        object[key] = config[key];

                        return object;
                    }, {});
            },

            /**
             * Set the minimum start date to today
             *
             * @returns {string}
             */
            minStartDate() {
                let startDate = new moment();

                // if there's no availability set, like on the homepage, return an empty array
                let unavailability = get(this, 'availability.unavailable', []);

                unavailability = unavailability
                    .map((date) => new moment(date).format('YYYY-MM-DD'));

                if (this.rental === 'long') {
                    if (this.space) {
                        startDate.add(this.space.notice_periods.long_term, 'hours');
                    }
                } else {
                    while (unavailability.includes(startDate.format('YYYY-MM-DD'))) {
                        startDate.add(1, 'day');
                    }
                }

                return startDate.format('DD-MM-YYYY');
            },

            /**
             * Constrain the maximum start date
             *
             * @returns {string}
             */
            maxStartDate() {

            },

            /**
             * Set the minimum start time.
             *
             * @param start_date
             *
             * @returns {boolean|string}
             */
            minStartTime(start_date) {
                // Set a constrain if it's todays date
                if (start_date === new moment().format('DD-MM-YYYY')) {
                    // Ensure we are rounded to the closest half hour
                    const start = new moment().floor(30, 'minute');
                    // We have selected today
                    return start.format('HH:mm');
                }

                return new moment('00:30', 'HH:mm').format('HH:mm');
            },

            /**
             * Set the maximum start time.
             *
             * @param start_date
             * @returns {boolean|string}
             */
            maxStartTime(start_date) {

            },

            /**
             * Make sure the end date is after the start
             *
             * @param start_date
             * @returns {string}
             */
            minEndDate(start_date) {
                // if user selected last available time of the day
                // then the minimum ending date needs to be moved to next day
                if (this.start_time && this.start_time === '23:59') {
                    return moment(`${this.start_date} ${this.start_time}`, 'DD-MM-YYYY HH:mm').add(1, 'days').format('DD-MM-YYYY');
                }
                return start_date;
            },

            /**
             * Constrain the end date
             *
             * @param start_date
             * @returns {string}
             */
            maxEndDate(start_date) {

            },

            /**
             * Make sure the end time follows our rules
             *
             * @param start_date
             * @param start_time
             * @param end_date
             * @returns {string}
             */
            minEndTime(start_date, start_time, end_date) {
                if (start_time === '23:59') {
                    return moment(start_time, 'HH:mm').add(30, 'minute').ceil(30, 'minutes').format('HH:mm');
                }

                if (start_date === end_date) {
                    if (start_time === '23:30') {
                        // look for timepicker component if confused..
                        return '23:45';
                    }
                    return moment(start_time, 'HH:mm').add(30, 'minute').format('HH:mm');
                }

                // Day in the future, allow any time selection
                return moment('00:00', 'HH:mm').format('HH:mm');
            },

            /**
             * Make sure the end time follows our rules
             *
             * @param start_date
             * @param start_time
             * @param end_date
             * @returns {boolean}
             */
            maxEndTime(start_date, start_time, end_date) {

            },

            /**
             * Make sure the start time is in the future
             */
            constrainStartTime() {
                const currentStartTime = new moment(`${this.start_date} ${this.start_time}`, 'DD-MM-YYYY HH:mm');

                // If we have a time in the past, adjust it
                if (currentStartTime.isBefore(minimumShortTermDate())) {
                    this.start_time = minimumShortTermDate().format('HH:mm');
                }
            },

            /**
             * Make sure the end date is valid
             */
            constrainEndDateTime() {
                const startDateTime = new moment(`${this.start_date} ${this.start_time}`, 'DD-MM-YYYY HH:mm');
                const endDateTime = new moment(`${this.end_date} ${this.end_time}`, 'DD-MM-YYYY HH:mm');

                // Verify end is not before start (date)
                if (endDateTime.isBefore(startDateTime)) {
                    this.end_date = this.start_date;
                }

                if (this.space && this.$store.state.booking.space.id && !this.allowsOvernight) {
                    // Not allowed overnight, ensure the start_date == end_date
                    this.end_date = this.start_date;
                }

                if (this.start_date === this.end_date) {
                    // We have the same day, ensure the time is later than the start_time
                    const endDateTime = new moment(`${this.end_date} ${this.end_time}`, 'DD-MM-YYYY HH:mm');
                    const startDateInHalfHour = startDateTime.add(30, 'minutes').ceil(30, 'minutes');

                    // If we have a time in the past (adding 30mins), adjust it
                    if (endDateTime.isSameOrBefore(startDateInHalfHour)) {
                        // in the last 30 minutes of the day, we still want to keep the same day for the end date
                        // however we want to select 23:59
                        if (this.start_time === '23:30') {
                            this.end_time = '23:59';
                        } else {
                            this.end_date = startDateInHalfHour.format('DD-MM-YYYY');
                            this.end_time = startDateInHalfHour.format('HH:mm');
                        }
                    } else {
                        this.end_date = endDateTime.format('DD-MM-YYYY');
                        this.end_time = endDateTime.format('HH:mm');
                    }
                }
            },

            constrainEndDateTimePayByPhone() {
                const startDateTime = new moment(`${this.start_date} ${this.start_time}`, 'DD-MM-YYYY HH:mm');
                let endDateTime = new moment(`${this.end_date} ${this.end_time}`, 'DD-MM-YYYY HH:mm');

                if (endDateTime.isAfter(startDateTime)) { return; }

                if (endDateTime.isBefore(startDateTime)) {
                    endDateTime = startDateTime.add(2, 'hours');
                }

                this.end_date = endDateTime.format('DD-MM-YYYY');
                this.end_time = endDateTime.format('HH:mm');
            },

            /**
             * depending on the type of booking chosen, assign the correct start date constraints
             *
             * @param {number} limit
             * @param {boolean} prebook
             * @param {boolean} postbook
             *
             * @returns {*|{minDate: string}|{minDate: string, maxDate: string}}
             */
            trueStartDateConfig(limit, prebook, postbook) {
                // pre book only today or any time from set by admin
                if (prebook){
                    return {
                        minDate: limit === 24 ? moment().format('DD-MM-YYYY') : moment().subtract(limit - 24, 'h').format('DD-MM-YYYY'),
                        maxDate: moment().format('DD-MM-YYYY'),
                    };
                }

                // book any time from to today
                if (postbook && limit === 24) {
                    return {
                        minDate: moment().format('DD-MM-YYYY'),
                    };
                }

                // book any time from set by admin
                if (postbook && limit !== 24) {
                    return {
                        minDate: moment().subtract(limit - 24, 'h').format('DD-MM-YYYY'),
                    };
                }

                return this.startDateConfig;
            },

            /**
             * depending on the type of booking chosen, assign the correct start time constraints
             *
             * @param {boolean} prebook
             * @param {boolean} postbook
             *
             * @returns {*|{min: string}|{min: string, max: string}}
             */
            trueStartTimeConfig(prebook, postbook) {
                if (prebook) {
                    return {
                        min: moment('00:00', 'HH:mm').format('HH:mm'),
                        max: moment(new Date()).format('HH:mm'),
                    };
                }

                if (postbook) {
                    return {
                        min: moment('00:00', 'HH:mm').format('HH:mm'),
                        max: moment('23:59', 'HH:mm').format('HH:mm'),
                    };
                }

                return this.startTimeConfig;
            },
        },
    };
</script>
